import { useEffect, useMemo, useCallback, useRef, useState } from "react"
import PropTypes from "prop-types"
import classnames from "classnames"
import { map, startCase, size, noop } from "lodash"
import { Search } from "@mui/icons-material"
import { ClickAwayListener, List, Divider, Tabs, Tab, Popper, Paper, TextField, InputAdornment, CircularProgress } from "@mui/material"
import { useSaveRecentInstrument } from "hooks/use-save-recent-instrument"
import { LOADING, OK, ERROR } from "helpers/data-statuses"
import { SECURITY_IDS } from "components/add-filter/shapes"
import SearchResult from "./components/search-result"
import { RECENT_INSTRUMENT_SEARCH_TYPES } from "resources/instruments"

import "./styles.scss"

export default function MainSearch({
  account,
  modelId,
  isOpen = false,
  value,
  items,
  selectedItem,
  skipClose,
  close,
  open,
  search,
  up,
  down,
  dispatch,
  onSelect = noop,
  emptyContent,
  autoFocus,
  disableResult = noop,
  compositeAssetId,
  compositeAssetType,
  endDate,
  tabs,
  selectedTab,
  setSelectedTab,
  allowedTypes,
  placeholder,
  disabled,
  disablePortal = false,
  variant = "standard",
  searchStatus,
  securityIds,
  multiSelect = false,
  resultSelected = noop,
  searchType = RECENT_INSTRUMENT_SEARCH_TYPES.GENERAL,
}) {
  const searchTimeoutRef = useRef(null)

  const [localValue, setLocalValue] = useState(value || "")
  const [showSearchResults, setShowSearchResults] = useState(false)
  const { saveRecentInstrument } = useSaveRecentInstrument(modelId)

  useEffect(() => {
    setLocalValue(value || "")
  }, [value])

  useEffect(() => {
    if (!isOpen) {
      setShowSearchResults(false)
    } else {
    // We need to give the trade menu time to expand before showing recent results or the placement is off
      const timeout = items?.length > 0 && searchType === RECENT_INSTRUMENT_SEARCH_TYPES.REBALANCE ? 500 : 0
      const timeoutId = setTimeout(() => setShowSearchResults(true), timeout)
      return () => clearTimeout(timeoutId)
    }
  }, [isOpen, searchType, items])

  const debouncedSearch = useCallback((searchValue) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }
    searchTimeoutRef.current = setTimeout(() => {
      search(account, modelId, searchValue, compositeAssetId, compositeAssetType, allowedTypes, endDate, securityIds)
    }, 300)
  }, [account, modelId, search, compositeAssetId, compositeAssetType, allowedTypes, endDate, securityIds])

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [])

  useEffect(() => {
    if (value) {
      search(account, modelId, value, compositeAssetId, compositeAssetType, allowedTypes, endDate, securityIds)
    }
  }, [value, modelId, search, account, compositeAssetId, compositeAssetType, allowedTypes, endDate, securityIds])

  const handleAction = useCallback((action, noDispatch, noOtherAction, item) => {
    if (action) {
      if (!noDispatch) {
        dispatch(action())
      } else {
        action()
      }

      if (!noOtherAction) {
        if (!multiSelect) {
          close()
        }
        onSelect()
      }
    }
    if (saveRecentInstrument && searchType && item) {
      saveRecentInstrument(item, searchType)
    }
  }, [close, dispatch, onSelect, multiSelect, searchType, saveRecentInstrument])

  function onKeyPress(event) {
    switch (event.key) {
      case "ArrowDown":
        down()
        break
      case "ArrowUp":
        up()
        break
      case "Escape":
        close()
        event.target.blur()
        break
      case "Enter": {
        if (selectedItem?.mainAction) {
          handleAction(selectedItem.mainAction, selectedItem.noDispatch)
        }
        break
      }
    }
  }

  function onChange(event) {
    const { value } = event.target
    setLocalValue(value)
    open()
    debouncedSearch(value)
  }

  function onClickAway() {
    if (isOpen && !skipClose) {
      close()
    }
  }

  function handleSetSelectedTab(_, value) {
    setSelectedTab(value)
  }

  const searchResultsElement = useMemo(() => {
    if (size(items) === 0) { return null }
    return <>
      <List component="nav">
        {map(items, item => (<SearchResult key={`${item.type}_${item.id}`} item={item} disableResult={disableResult} handleAction={handleAction} multiSelect={multiSelect} resultSelected={resultSelected} value={value} />))}
      </List>
      <Divider />
    </>
  }, [disableResult, items, handleAction, multiSelect, resultSelected, value])

  const [anchorNode, setAnchorNode] = useState(null)
  const [anchorWidth, setAnchorWidth] = useState()

  useEffect(() => {
    if (!anchorNode) { return undefined }
    // Track the input width so the results Popper matches it on mount and resize —
    // reading anchorNode.clientWidth during render is a react-hooks/refs violation and goes stale.
    const observer = new ResizeObserver(([entry]) => setAnchorWidth(entry.target.clientWidth))
    observer.observe(anchorNode)
    return () => observer.disconnect()
  }, [anchorNode])

  const isLoading = searchStatus === LOADING
  const loaderVisible = value !== localValue || isLoading

  return (
    <ClickAwayListener onClickAway={onClickAway}>
      <div className={classnames("modules_unified-search", { open: isOpen && items.length, focused: isOpen })}>
        <TextField
          fullWidth
          variant={variant}
          ref={setAnchorNode}
          tabIndex={0}
          onFocus={open}
          onKeyDown={onKeyPress}
          placeholder={placeholder ?? "Search…"}
          disabled={disabled}
          value={localValue}
          autoFocus={autoFocus}
          onChange={onChange}
          slotProps={{
            root: {
              className: "search-input-wrapper",
            },
            htmlInput: {
              className: "search-input",
            },
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <CircularProgress size={25} style={{ visibility: loaderVisible ? "visible" : "hidden" }} className={classnames({ "search-loading": loaderVisible })} />
                </InputAdornment>
              ),
            },
          }}
        />
        <Popper disablePortal={disablePortal} anchorEl={anchorNode} open={showSearchResults} placement="bottom-start" style={{ minWidth: anchorWidth }} className={classnames("unified-search-popper", { "disable-portal": disablePortal })}>
          <Paper square elevation={disablePortal ? 0 : 3}>
            {tabs && <Tabs
              scrollButtons="auto"
              variant="scrollable"
              className="tabs"
              value={selectedTab}
              indicatorColor="primary"
              textColor="primary"
              onChange={handleSetSelectedTab}
            >
              {map(tabs, ({ id, count, disabled }) => <Tab key={id} value={id} label={`${startCase(id)} (${count})`} disabled={disabled} />)}
            </Tabs>}
            {showSearchResults && <div className="search-box">
              {((!value || isLoading) && !searchResultsElement) && emptyContent}
              {value && !searchResultsElement && !isLoading && <div className="unified-search-no-results">No results found</div>}
              {searchResultsElement}
            </div>}
          </Paper>
        </Popper>
      </div>
    </ClickAwayListener>
  )
}

MainSearch.propTypes = {
  account: PropTypes.string.isRequired,
  modelId: PropTypes.string.isRequired,
  isOpen: PropTypes.bool.isRequired,
  value: PropTypes.string.isRequired,
  items: PropTypes.array,
  selectedItem: PropTypes.object,
  skipClose: PropTypes.bool,
  close: PropTypes.func.isRequired,
  open: PropTypes.func.isRequired,
  search: PropTypes.func.isRequired,
  up: PropTypes.func.isRequired,
  down: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
  onSelect: PropTypes.func,
  emptyContent: PropTypes.node,
  autoFocus: PropTypes.bool,
  disableResult: PropTypes.func,
  compositeAssetId: PropTypes.string,
  compositeAssetType: PropTypes.string,
  endDate: PropTypes.string,
  tabs: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    count: PropTypes.number.isRequired,
    disabled: PropTypes.bool.isRequired,
  })),
  selectedTab: PropTypes.string.isRequired,
  setSelectedTab: PropTypes.func.isRequired,
  allowedTypes: PropTypes.arrayOf(PropTypes.oneOf(["benchmark", "currency", "index", "portfolio", "security", "swap"])),
  hiddenTypes: PropTypes.arrayOf(PropTypes.oneOf(["benchmarks", "experiments", "factors", "researchTopics", "watchlists", "currencies"])),
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  disablePortal: PropTypes.bool,
  searchStatus: PropTypes.oneOf([LOADING, OK, ERROR]),
  variant: PropTypes.oneOf(["standard", "outlined", "filled"]),
  securityIds: SECURITY_IDS,
  multiSelect: PropTypes.bool,
  resultSelected: PropTypes.func,
  searchType: PropTypes.string,
}
